import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import config from '../../config/environment';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import { 
  ArrowRight, 
  ArrowLeft,
  Check,
  X,
  Upload,
  Download,
  FileText,
  Users,
  Settings,
  Eye,
  AlertCircle,
  CheckCircle,
  XCircle,
  Plus,
  Layers,
  Palette,
  Database,
  Mail,
  Calendar,
  Globe,
  Shield,
  Clock,
  Target,
  BarChart3
} from 'lucide-react';
import W3CSchemaBuilder from './W3CSchemaBuilder';
import CredentialTemplateDesigner from './CredentialTemplateDesigner';
import { CredentialSchema, CredentialTemplate, CredentialFieldDefinition } from '../../types/W3CCredentialTypes';
import { getAuthToken } from '@/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { validateCsvData, issueCredentialsBulk } from '../../utils/csvHandler';
import { useNotification } from '../../contexts/NotificationContext';
import { FullPageLoader } from '../ui/full-page-loader';

interface WizardStep {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'current' | 'completed' | 'error';
  required: boolean;
}

interface ValidationResult {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  errors: ValidationError[];
  previewData: CredentialRecord[];
  allRecords: CredentialRecord[];
}

interface ValidationError {
  row: number;
  field: string;
  value: string;
  error: string;
  severity: 'error' | 'warning';
}

interface CredentialRecord {
  id: string;
  data: Record<string, string>;
  isValid: boolean;
  errors: string[];
}

interface EnhancedCredentialWizardProps {
  onComplete: () => void;
  onCancel: () => void;
}

export default function EnhancedCredentialWizard({ onComplete, onCancel }: EnhancedCredentialWizardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifyApiSuccess, notifyApiError } = useNotification();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedSchema, setSelectedSchema] = useState<CredentialSchema | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<CredentialTemplate | null>(null);
  const [issuanceMethod, setIssuanceMethod] = useState<'manual' | 'bulk'>('manual');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [manualData, setManualData] = useState<Record<string, string | boolean>>({});
  const [deliveryMethod, setDeliveryMethod] = useState<'email' | 'download' | 'api'>('email');
  const [credentialSchemas, setCredentialSchemas] = useState<CredentialSchema[]>([]);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [isIssuing, setIsIssuing] = useState(false);
  const [isSchemasLoading, setIsSchemasLoading] = useState(false);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);

  // Helper function to generate appropriate placeholders based on field type
  const getFieldPlaceholder = (field: CredentialFieldDefinition): string => {
    if (field.placeholder) return field.placeholder;
    
    switch (field.type) {
      case 'email': return 'Enter email address';
      case 'number':
      case 'decimal': return 'Enter number';
      case 'date': return 'Select date';
      case 'boolean':
      case 'bool': return 'Select Yes or No';
      case 'url': return 'Enter URL';
      case 'phone': return 'Enter phone number';
      default: return `Enter ${field.displayName || field.name}`;
    }
  };

  useEffect(() => {
      async function fetchCredentialSchemas() {
          try {
              setIsSchemasLoading(true);
              const API_BASE_URL = config.REACT_APP_API_BASE_URL;
              const response = await fetch(`${API_BASE_URL}/api/credential-schemas/`, {
                  method: 'GET',
                  headers: {
                      'Authorization': `Bearer ${getAuthToken()}`,
                      'Content-Type': 'application/json',
                  },
              });
  
              if (!response.ok) {
                  throw new Error(`Error fetching credential schemas: ${response.statusText}`);
              }
  
              const data = await response.json();
              setCredentialSchemas(data);
          } catch (error) {
              console.error(error);
          } finally {
              setIsSchemasLoading(false);
          }
      }
  
      fetchCredentialSchemas();
  }, []);
  
  useEffect(() => {
      async function fetchSchemaFields() {
          if (issuanceMethod === 'manual' && selectedSchema) {
              try {
                  const API_BASE_URL = config.REACT_APP_API_BASE_URL;
                  const response = await fetch(`${API_BASE_URL}/api/credential-schemas/${selectedSchema.id}/fields`, {
                      method: 'GET',
                      headers: {
                          'Authorization': `Bearer ${getAuthToken()}`,
                          'Content-Type': 'application/json',
                      },
                  });
  
                  if (!response.ok) {
                      throw new Error(`Error fetching schema fields: ${response.statusText}`);
                  }
  
                  const fields = await response.json();
                  setSelectedSchema(prev => {
                      if (prev) {
                          const formattedFields = Object.entries(fields).map(([name, details]) => {
                              const fieldDetails = details as { type: "number" | "boolean" | "email" | "text" | "date" | "url" | "phone" | "select" | "textarea" }; // Type assertion for details
                              const mappedType = fieldDetails.type; // Directly use the type since it's already validated
                              return {
                                  name,
                                  type: mappedType,
                                  displayName: name.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()),
                                  required: true, // Assuming all fields are required unless specified otherwise
                                  placeholder: `Enter ${name.replace(/_/g, ' ')}`,
                                  description: undefined // Updated to match expected type
                              };
                          });
                          return { ...prev, fields: formattedFields };
                      }
                      return prev;
                  });
              } catch (error) {
                  console.error(error);
              }
          }
      }
  
      fetchSchemaFields();
  }, [issuanceMethod, selectedSchema?.id]);

  const [steps, setSteps] = useState<WizardStep[]>([
    {
      id: 'schema',
      title: 'Select or Create Schema',
      description: 'Choose the data structure for your credentials',
      status: 'current',
      required: true
    },
    {
      id: 'template',
      title: 'Select or Create Template',
      description: 'Design the visual appearance of your credentials',
      status: 'pending',
      required: true
    },
    {
      id: 'method',
      title: 'Choose Issuance Method',
      description: 'Manual entry or bulk upload from CSV',
      status: 'pending',
      required: true
    },
    {
      id: 'data',
      title: 'Provide Credential Data',
      description: 'Enter or upload the credential information',
      status: 'pending',
      required: true
    },
    {
      id: 'validation',
      title: 'Review & Validate',
      description: 'Review data and validate against schema',
      status: 'pending',
      required: true
    },
    {
      id: 'delivery',
      title: 'Configure Delivery',
      description: 'Set up how credentials will be delivered',
      status: 'pending',
      required: true
    },
    {
      id: 'issue',
      title: 'Issue Credentials',
      description: 'Generate and distribute the credentials',
      status: 'pending',
      required: true
    }
  ]);

  // Mock schemas for selection
  // Removed hardcoded availableSchemas and replaced with fetched credentialSchemas

  // Mock templates for selection
  const [fetchedTemplates, setFetchedTemplates] = useState<CredentialTemplate[]>([]);

  useEffect(() => {
    async function fetchTemplates() {
      if (selectedSchema?.template_file_id) {
        try {
          setIsTemplatesLoading(true);
          const API_BASE_URL = config.REACT_APP_API_BASE_URL;
          const response = await fetch(`${API_BASE_URL}/api/templates/files/${selectedSchema.template_file_id}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${getAuthToken()}`,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error(`Error fetching templates: ${response.statusText}`);
          }

          const data = await response.json();
          setFetchedTemplates([data]);
        } catch (error) {
          console.error(error);
        } finally {
          setIsTemplatesLoading(false);
        }
      }
    }

    fetchTemplates();
  }, [selectedSchema]);

  const [error, setError] = useState('');
  const [issuanceProgress, setIssuanceProgress] = useState(0);
  const [bulkIssuanceErrors, setBulkIssuanceErrors] = useState<string[]>([]);
  
  const handleIssueCredential = async () => {
    if (!selectedSchema) return;
    
    setIsIssuing(true);
    setBulkIssuanceErrors([]);
    setIssuanceProgress(0);
    setError('');
    
    try {
      if (issuanceMethod === 'manual') {
        // Single credential issuance
        const API_BASE_URL = config.REACT_APP_API_BASE_URL;

                // Transform credential fields to ensure proper types
        const transformedFields: Record<string, string | boolean | number> = {};
        selectedSchema.fields.forEach(field => {
          const value = manualData[field.name];
          if (value !== undefined && value !== null && value !== '') {
            // Convert number and decimal types to actual numbers
            if (field.type === 'number' || field.type === 'decimal') {
              transformedFields[field.name] = typeof value === 'string' ? parseFloat(value) : value;
            } else {
              transformedFields[field.name] = value;
            }
          }
        });
        const credentialData = {
          credential_schema_id: selectedSchema.id,
          holder_email: manualData['holder_email'],
          credential_fields: transformedFields,
        };

        const response = await fetch(`${API_BASE_URL}/api/credentials/create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(credentialData),
        });

        if (!response.ok) {
          throw new Error(`Error issuing credential: ${response.statusText}`);
        }

        await response.json();
        notifyApiSuccess('Credential', 'issued', String(credentialData.holder_email || ''));
      } else {
        // Bulk issuance
        if (!validationResult) throw new Error('No validation results available');
        
        // Get all records, not just preview data
        const allValidRecords = validationResult.allRecords.filter(record => record.isValid);
        if (allValidRecords.length === 0) {
          throw new Error('No valid records to process');
        }

        const result = await issueCredentialsBulk(
          allValidRecords,
          selectedSchema,
          (progress) => setIssuanceProgress(progress)
        );

        if (!result.success) {
          setBulkIssuanceErrors(result.errors || []);
          if (allValidRecords.length === result.errors?.length) {
            throw new Error('All credential issuance attempts failed');
          }

          notifyApiError(
            'Credentials',
            'issue',
            result.errors && result.errors.length > 0
              ? result.errors[0]
              : 'Some credentials failed to issue'
          );
        } else {
          notifyApiSuccess(
            'Credentials',
            'issued',
            `${allValidRecords.length} credentials issued successfully`
          );
        }
      }
      
      // Show success alert
      setShowSuccessAlert(true);
      
    } catch (err) {
      console.error('Failed to issue credentials:', err);
      const message = err instanceof Error ? err.message : 'Failed to issue credentials';
      setError(message);
      notifyApiError('Credentials', 'issue', message);
    } finally {
      setIsIssuing(false);
      setIssuanceProgress(0);
    }
  };

  const handleSuccessAlertClose = () => {
    setShowSuccessAlert(false);
    onComplete(); // Call the original completion callback
    
    // Navigate to home page based on user role
    if (user?.role) {
      const roleRoutes = {
        'issuer': '/issuer',
        'admin': '/admin', 
        'super_admin': '/admin',
        'recipient': '/recipient',
        'verifier': '/verifier'
      };
      
      const route = roleRoutes[user.role as keyof typeof roleRoutes] || '/issuer';
      navigate(route);
    } else {
      navigate('/');
    }
  };

  const updateStepStatus = (stepIndex: number, status: WizardStep['status']) => {
    setSteps(prev => prev.map((step, index) => 
      index === stepIndex ? { ...step, status } : step
    ));
  };

  const handleCsvValidation = async (file: File, schema: CredentialSchema): Promise<void> => {
    setIsValidating(true);
    setError('');
    
    try {
      const result = await validateCsvData(file, schema);
      setValidationResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to validate CSV file');
      setCsvFile(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleCsvUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && selectedSchema) {
      setCsvFile(file);
      await handleCsvValidation(file, selectedSchema);
    }
  };

  const handleNext = () => {
    updateStepStatus(currentStep, 'completed');
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      updateStepStatus(currentStep + 1, 'current');
    }
  };

  const handlePrevious = () => {
    updateStepStatus(currentStep, 'pending');
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      updateStepStatus(currentStep - 1, 'current');
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return selectedSchema !== null;
      case 1: return selectedTemplate !== null;
      case 2: return issuanceMethod !== null;
      case 3: 
        if (issuanceMethod === 'manual') {
          // Check all required fields are filled
          const requiredFields = selectedSchema?.fields.filter(f => f.required) || [];
          return requiredFields.every(field => {
            const value = manualData[field.name];
            // For boolean fields, check if value is explicitly set (true or false)
            if (field.type === 'boolean' || field.type === 'bool') {
              return value !== undefined && value !== null && value !== '';
            }
            // For other fields, check if value exists and is not empty
            return value !== undefined && value !== null && value !== '';
          });
        } else {
          return csvFile !== null;
        }
      case 4: 
        if (issuanceMethod === 'bulk') {
          return validationResult !== null && validationResult.validRows > 0;
        }
        return true;
      case 5: return deliveryMethod !== null;
      default: return true;
    }
  };

  const progressPercentage = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6 relative">
      {currentStep === 0 && isSchemasLoading && (
        <FullPageLoader message="Loading schemas..." />
      )}
      {currentStep === 1 && isTemplatesLoading && (
        <FullPageLoader message="Loading templates..." />
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-charcoal-ink">
            Issue Credentials
          </h1>
          <p className="text-warm-slate">
            Complete wizard to create and issue W3C-compliant credentials
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>

      {/* Progress Bar */}
      <Card className="credential-capital-card">
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-charcoal-ink">
                Step {currentStep + 1} of {steps.length}
              </span>
              <span className="text-sm text-warm-slate">
                {Math.round(progressPercentage)}% Complete
              </span>
            </div>
            <Progress value={progressPercentage} className="w-full" />
            <div className="flex items-center justify-between text-xs text-warm-slate">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center space-x-1">
                  <div className={`w-2 h-2 rounded-full ${
                    step.status === 'completed' ? 'bg-verified-green' :
                    step.status === 'current' ? 'bg-electric-steel' :
                    step.status === 'error' ? 'bg-red-500' :
                    'bg-gray-300'
                  }`} />
                  <span className={step.status === 'current' ? 'font-medium' : ''}>
                    {step.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step Content */}
      <Card className="credential-capital-card">
        <CardHeader>
          <CardTitle>{steps[currentStep].title}</CardTitle>
          <CardDescription>{steps[currentStep].description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step 0: Schema Selection */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <div className="grid gap-4">
                <h3 className="font-semibold text-charcoal-ink">Available Schemas</h3>
                {credentialSchemas.map(schema => (
                  <Card 
                    key={schema.id}
                    className={`cursor-pointer transition-all hover:shadow-lg ${
                      selectedSchema?.id === schema.id ? 'border-electric-steel bg-blue-50' : 'border-gray-200'
                    }`}
                    onClick={() => setSelectedSchema(schema)}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <div className="p-3 rounded-lg bg-electric-steel/10">
                            <Layers className="h-5 w-5 text-electric-steel" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-charcoal-ink">{schema.name}</h4>
                            <p className="text-sm text-warm-slate mb-2">{schema.description}</p>
                            <div className="flex items-center space-x-4 text-sm text-warm-slate">
                              <span>Version: {schema.version}</span>
                              <span>Fields: {schema.fields.length}</span>
                              <span>Required: {schema.fields.filter(f => f.required).length}</span>
                            </div>
                          </div>
                        </div>
                        {selectedSchema?.id === schema.id && (
                          <CheckCircle className="h-5 w-5 text-verified-green" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <Separator />
              
              <div className="text-center">
                <p className="text-sm text-warm-slate mb-4">Don't see the schema you need?</p>
                <Button variant="outline" className="credential-capital-button-secondary">
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Schema
                </Button>
              </div>
            </div>
          )}

          {/* Step 1: Template Selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="grid gap-4">
                <h3 className="font-semibold text-charcoal-ink">Available Templates</h3>
                {fetchedTemplates
                  .map(template => (
                  <Card 
                    key={template.id}
                    className={`cursor-pointer transition-all hover:shadow-lg ${
                      selectedTemplate?.id === template.id ? 'border-electric-steel bg-blue-50' : 'border-gray-200'
                    }`}
                    onClick={() => setSelectedTemplate(template)}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <div className="p-3 rounded-lg bg-electric-steel/10">
                            <Palette className="h-5 w-5 text-electric-steel" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-charcoal-ink">{selectedSchema?.name}</h4>
                            <p className="text-sm text-warm-slate mb-2">{selectedSchema?.description}</p>
                            {/* <div className="flex items-center space-x-4 text-sm text-warm-slate">
                              <span>Size: {template.width}x{template.height}</span>
                              <span>Fields: {template.fieldPositions.length}</span>
                            </div> */}
                          </div>
                        </div>
                        {selectedTemplate?.id === template.id && (
                          <CheckCircle className="h-5 w-5 text-verified-green" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <Separator />
              
              <div className="text-center">
                <p className="text-sm text-warm-slate mb-4">Need a custom template design?</p>
                <Button variant="outline" className="credential-capital-button-secondary">
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Template
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Issuance Method */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h3 className="font-semibold text-charcoal-ink">Choose Issuance Method</h3>
              <div className="grid grid-cols-2 gap-6">
                <Card 
                  className={`cursor-pointer transition-all hover:shadow-lg ${
                    issuanceMethod === 'manual' ? 'border-electric-steel bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => setIssuanceMethod('manual')}
                >
                  <CardContent className="pt-6 text-center">
                    <div className="p-4 rounded-lg bg-electric-steel/10 w-fit mx-auto mb-4">
                      <FileText className="h-8 w-8 text-electric-steel" />
                    </div>
                    <h4 className="font-semibold text-charcoal-ink mb-2">Manual Entry</h4>
                    <p className="text-sm text-warm-slate mb-4">
                      Enter credential information manually for individual or small batch issuance
                    </p>
                    <Badge variant="outline">Best for 1-10 credentials</Badge>
                  </CardContent>
                </Card>

                <Card 
                  className={`cursor-pointer transition-all hover:shadow-lg ${
                    issuanceMethod === 'bulk' ? 'border-electric-steel bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => setIssuanceMethod('bulk')}
                >
                  <CardContent className="pt-6 text-center">
                    <div className="p-4 rounded-lg bg-electric-steel/10 w-fit mx-auto mb-4">
                      <Database className="h-8 w-8 text-electric-steel" />
                    </div>
                    <h4 className="font-semibold text-charcoal-ink mb-2">Bulk Upload</h4>
                    <p className="text-sm text-warm-slate mb-4">
                      Upload a CSV file with multiple credential records for batch processing
                    </p>
                    <Badge variant="outline">Best for 10+ credentials</Badge>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Step 3: Data Entry */}
          {currentStep === 3 && (
            <div className="space-y-6">
              {issuanceMethod === 'manual' ? (
                  <div className="space-y-4">
                      <h3 className="font-semibold text-charcoal-ink">Enter Credential Data</h3>
                      <div className="grid grid-cols-2 gap-4">
                          {selectedSchema?.fields?.map((field) => {
                              console.log(`Field: ${field.name}, Type: ${field.type}, Is Boolean: ${field.type === 'boolean'}`);
                              if (field.name === 'holder_email') {
                                  return (
                                      <div key={field.name}>
                                          <Label htmlFor={field.name}>
                                              {field.displayName || field.name}
                                              {field.required && <span className="text-red-500 ml-1">*</span>}
                                          </Label>
                                          <Input
                                              id={field.name}
                                              type="email"
                                              placeholder={getFieldPlaceholder(field)}
                                              value={(typeof manualData[field.name] === 'boolean' ? '' : manualData[field.name]) as string || ''}
                                              onChange={(e) => {
                                                  const value = e.target.value;
                                                  setManualData(prev => ({
                                                      ...prev,
                                                      [field.name]: value
                                                  }));
                                              }}
                                              className={`mt-1 ${field.required && !manualData[field.name] ? 'border-red-500' : ''}`}
                                          />
                                          {field.description && (
                                              <p className="text-xs text-warm-slate mt-1">{field.description}</p>
                                          )}
                                          {field.required && !manualData[field.name] && (
                                              <p className="text-xs text-red-500 mt-1">This field is required.</p>
                                          )}
                                      </div>
                                  );
                              }
                              return (
                                  <div key={field.name}>
                                      <Label htmlFor={field.name}>
                                          {field.displayName || field.name}
                                          {field.required && <span className="text-red-500 ml-1">*</span>}
                                      </Label>
                                      {(field.type === 'boolean' || field.type === 'bool') ? (
                                          <Select
                                              value={manualData[field.name]?.toString() || ''}
                                              onValueChange={(value) => {
                                                  setManualData(prev => ({
                                                      ...prev,
                                                      [field.name]: value === 'true'
                                                  }));
                                              }}
                                          >
                                              <SelectTrigger className={`mt-1 ${field.required && manualData[field.name] === undefined ? 'border-red-500' : ''}`}>
                                                  <SelectValue placeholder={getFieldPlaceholder(field)} />
                                              </SelectTrigger>
                                              <SelectContent>
                                                  <SelectItem value="true">Yes</SelectItem>
                                                  <SelectItem value="false">No</SelectItem>
                                              </SelectContent>
                                          </Select>
                                      ) : (
                                          <Input
                                              id={field.name}
                                              type={field.type === 'date' ? 'date' : (field.type === 'number' || field.type === 'decimal') ? 'number' : field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : field.type === 'phone' ? 'tel' : 'text'}
                                              placeholder={getFieldPlaceholder(field)}
                                              value={(typeof manualData[field.name] === 'boolean' ? '' : manualData[field.name]) as string || ''}
                                              onChange={(e) => {
                                                  const value = e.target.value;
                                                  setManualData(prev => ({
                                                      ...prev,
                                                      [field.name]: value
                                                  }));
                                              }}
                                              className={`mt-1 ${field.required && !manualData[field.name] ? 'border-red-500' : ''}`}
                                          />
                                      )}
                                      {field.description && (
                                          <p className="text-xs text-warm-slate mt-1">{field.description}</p>
                                      )}
                                      {field.required && ((field.type === 'boolean' || field.type === 'bool') ? manualData[field.name] === undefined : !manualData[field.name]) && (
                                          <p className="text-xs text-red-500 mt-1">This field is required.</p>
                                      )}
                                  </div>
                              );
                          })}
                      </div>
                  </div>
              ) : (
                <div className="space-y-6">
                  <h3 className="font-semibold text-charcoal-ink">Upload CSV File</h3>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <div className="space-y-2">
                      <p className="text-lg font-medium text-charcoal-ink">Upload your CSV file</p>
                      <p className="text-sm text-warm-slate">
                        File should contain columns matching your schema fields
                      </p>
                    </div>
                    <Input
                      type="file"
                      accept=".csv"
                      onChange={handleCsvUpload}
                      className="mt-4 max-w-xs mx-auto"
                    />
                  </div>

                  {csvFile && (
                    <Alert>
                      <FileText className="h-4 w-4" />
                      <AlertDescription>
                        File uploaded: {csvFile.name} ({(csvFile.size / 1024).toFixed(1)} KB)
                      </AlertDescription>
                    </Alert>
                  )}

                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        {error}
                      </AlertDescription>
                    </Alert>
                  )}

                  {isValidating && (
                    <Alert>
                      <Clock className="h-4 w-4" />
                      <AlertDescription>
                        Validating CSV file...
                      </AlertDescription>
                    </Alert>
                  )}


                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-charcoal-ink mb-2">CSV Format Requirements</h4>
                    <div className="text-sm text-warm-slate space-y-1">
                      <p>• First row should contain column headers</p>
                      <p>• Required columns: {selectedSchema?.fields.filter(f => f.required).map(f => f.name).join(', ')}</p>
                      <p>• Optional columns: {selectedSchema?.fields.filter(f => !f.required).map(f => f.name).join(', ')}</p>
                    </div>
                    <Button variant="outline" size="sm" className="mt-3">
                      <Download className="h-4 w-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Validation Results */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h3 className="font-semibold text-charcoal-ink">Validation Results</h3>
              
              {issuanceMethod === 'bulk' && validationResult ? (
                <>
                  {/* Validation Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="border">
                      <CardContent className="pt-6 text-center">
                        <div className="p-3 rounded-lg bg-blue-50 w-fit mx-auto mb-2">
                          <Database className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="text-2xl font-bold text-charcoal-ink">{validationResult.totalRows}</div>
                        <div className="text-sm text-warm-slate">Total Records</div>
                      </CardContent>
                    </Card>

                    <Card className="border">
                      <CardContent className="pt-6 text-center">
                        <div className="p-3 rounded-lg bg-green-50 w-fit mx-auto mb-2">
                          <CheckCircle className="h-6 w-6 text-verified-green" />
                        </div>
                        <div className="text-2xl font-bold text-verified-green">{validationResult.validRows}</div>
                        <div className="text-sm text-warm-slate">Valid Records</div>
                      </CardContent>
                    </Card>

                    <Card className="border">
                      <CardContent className="pt-6 text-center">
                        <div className="p-3 rounded-lg bg-red-50 w-fit mx-auto mb-2">
                          <XCircle className="h-6 w-6 text-red-500" />
                        </div>
                        <div className="text-2xl font-bold text-red-500">{validationResult.invalidRows}</div>
                        <div className="text-sm text-warm-slate">Invalid Records</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Validation Progress */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Validation Progress</span>
                      <span>{Math.round((validationResult.validRows / validationResult.totalRows) * 100)}% Valid</span>
                    </div>
                    <Progress 
                      value={(validationResult.validRows / validationResult.totalRows) * 100} 
                      className="w-full"
                    />
                  </div>

                  {/* Validation Errors */}
                  {validationResult.errors.length > 0 && (
                    <Card className="border-red-200">
                      <CardHeader>
                        <CardTitle className="text-red-600 flex items-center">
                          <AlertCircle className="h-5 w-5 mr-2" />
                          Validation Errors ({validationResult.errors.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3 max-h-48 overflow-y-auto">
                          {validationResult.errors.map((error, index) => (
                            <div key={index} className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
                              <div className={`p-1 rounded-full ${
                                error.severity === 'error' ? 'bg-red-500' : 'bg-amber-500'
                              }`}>
                                <div className="w-2 h-2 bg-white rounded-full" />
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium text-charcoal-ink">
                                  Row {error.row}: {error.field}
                                </p>
                                <p className="text-sm text-warm-slate">
                                  {error.error}
                                </p>
                                {error.value && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Value: "{error.value}"
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Data Preview */}
                  <Card className="border">
                    <CardHeader>
                      <CardTitle>Data Preview</CardTitle>
                      <CardDescription>First few records from your upload</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {validationResult.previewData.map((record, index) => (
                          <div key={record.id} className={`p-4 rounded-lg border ${
                            record.isValid ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                          }`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-sm">Record {index + 1}</span>
                              <div className="flex items-center space-x-2">
                                {record.isValid ? (
                                  <Badge className="bg-verified-green text-white">Valid</Badge>
                                ) : (
                                  <Badge className="bg-red-500 text-white">Invalid</Badge>
                                )}
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              {Object.entries(record.data).map(([key, value]) => (
                                <div key={key}>
                                  <span className="text-warm-slate">{key}:</span>
                                  <span className="ml-2 text-charcoal-ink">{value}</span>
                                </div>
                              ))}
                            </div>
                            {record.errors.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-red-200">
                                <div className="space-y-1">
                                  {record.errors.map((error, errorIndex) => (
                                    <p key={errorIndex} className="text-xs text-red-600">• {error}</p>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="h-16 w-16 text-verified-green mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-charcoal-ink mb-2">Data Validated</h3>
                  <p className="text-warm-slate">Your credential data is ready for issuance</p>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Delivery Configuration */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <h3 className="font-semibold text-charcoal-ink">Configure Delivery Method</h3>
              <div className="grid gap-4">
                <Card 
                  className={`cursor-pointer transition-all hover:shadow-lg ${
                    deliveryMethod === 'email' ? 'border-electric-steel bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => setDeliveryMethod('email')}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 rounded-lg bg-electric-steel/10">
                        <Mail className="h-6 w-6 text-electric-steel" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-charcoal-ink">Email Delivery</h4>
                        <p className="text-sm text-warm-slate">Send credentials directly to recipients via email</p>
                      </div>
                      {deliveryMethod === 'email' && (
                        <CheckCircle className="h-5 w-5 text-verified-green ml-auto" />
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className={`cursor-pointer transition-all hover:shadow-lg ${
                    deliveryMethod === 'download' ? 'border-electric-steel bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => setDeliveryMethod('download')}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 rounded-lg bg-electric-steel/10">
                        <Download className="h-6 w-6 text-electric-steel" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-charcoal-ink">Download Package</h4>
                        <p className="text-sm text-warm-slate">Generate downloadable package with all credentials</p>
                      </div>
                      {deliveryMethod === 'download' && (
                        <CheckCircle className="h-5 w-5 text-verified-green ml-auto" />
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className={`cursor-pointer transition-all hover:shadow-lg ${
                    deliveryMethod === 'api' ? 'border-electric-steel bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => setDeliveryMethod('api')}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 rounded-lg bg-electric-steel/10">
                        <Globe className="h-6 w-6 text-electric-steel" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-charcoal-ink">API Integration</h4>
                        <p className="text-sm text-warm-slate">Deliver via API to your existing systems</p>
                      </div>
                      {deliveryMethod === 'api' && (
                        <CheckCircle className="h-5 w-5 text-verified-green ml-auto" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Step 6: Final Review and Issue */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <h3 className="font-semibold text-charcoal-ink">Review & Issue Credentials</h3>
              
              <div className="grid grid-cols-2 gap-6">
                <Card className="border">
                  <CardHeader>
                    <CardTitle className="text-sm">Credential Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-warm-slate">Schema:</span>
                      <span className="font-medium">{selectedSchema?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-warm-slate">Template:</span>
                      <span className="font-medium">{selectedTemplate?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-warm-slate">Method:</span>
                      <span className="font-medium capitalize">{issuanceMethod}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-warm-slate">Delivery:</span>
                      <span className="font-medium capitalize">{deliveryMethod}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border">
                  <CardHeader>
                    <CardTitle className="text-sm">Issuance Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-warm-slate">Total Credentials:</span>
                      <span className="font-medium">
                        {issuanceMethod === 'bulk' ? validationResult?.validRows || 0 : 1}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-warm-slate">Estimated Time:</span>
                      <span className="font-medium">2-5 minutes</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-warm-slate">Status:</span>
                      <Badge className="bg-verified-green text-white">Ready to Issue</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  All validations passed. Your credentials are ready to be issued and delivered.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button 
          variant="outline" 
          onClick={handlePrevious}
          disabled={currentStep === 0}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>
        
        <div className="flex space-x-2">
          {currentStep === steps.length - 1 ? (
            <Button 
              onClick={handleIssueCredential}
              className="credential-capital-button-primary"
              disabled={!canProceed() || isIssuing}
            >
              {isIssuing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Issuing...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Issue Credential
                </>
              )}
            </Button>
          ) : (
            <Button 
              onClick={handleNext}
              className="credential-capital-button-primary"
              disabled={!canProceed()}
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>

      {/* Success Alert Modal */}
      {showSuccessAlert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="credential-capital-card bg-white p-6 max-w-md w-full mx-4">
            <div className="text-center space-y-4">
              <div className="p-4 rounded-full bg-verified-green/10 w-fit mx-auto">
                <Check className="h-8 w-8 text-verified-green" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-charcoal-ink">Credential Issued Successfully!</h3>
                <p className="text-sm text-warm-slate mt-2">
                  {issuanceMethod === 'bulk' 
                    ? `${validationResult?.validRows || 0} credentials have been issued successfully.`
                    : 'The credential has been issued successfully.'
                  }
                </p>
              </div>
              <Button 
                onClick={handleSuccessAlertClose}
                className="credential-capital-button-primary w-full"
              >
                Continue to Dashboard
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Validation Loading */}
      {isValidating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="credential-capital-card p-6">
            <div className="text-center space-y-4">
              <div className="w-8 h-8 border-4 border-electric-steel border-t-transparent rounded-full animate-spin mx-auto"></div>
              <div>
                <h3 className="font-semibold text-charcoal-ink">Validating CSV Data</h3>
                <p className="text-sm text-warm-slate">Checking data against schema requirements...</p>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}